/**
 * Sprint 23.7 — `<WelcomeShell>`: the full-screen onboarding takeover that hosts
 * the three `/welcome` steps. Renders a dimmed tertiary canvas with a centered
 * step card (StepIndicator header + an `<Outlet/>` content body for the active
 * step).
 *
 * Guard (Q6 / S23.7.27): only NEW workspaces see onboarding. If the workspace
 * has already completed onboarding, or already has agents (every existing /
 * dogfood workspace), we redirect to `/workforce` — they never see `/welcome`.
 */

import { useEffect, useMemo, type CSSProperties } from "react";
import { Outlet, useLocation, useNavigate } from "react-router";
import { useAuth } from "../../../lib/hooks/use-auth";
import { useListTree, useWorkspaceInfo } from "../../../lib/hooks/use-kuvio";
import { useOnboardingComplete } from "../../hooks/use-onboarding";
import { StepIndicator } from "./StepIndicator";

const STEP_BY_PATH: Record<string, number> = {
  "/welcome/intent": 1,
  "/welcome/theme": 2,
  "/welcome/hire": 3,
};

export function WelcomeShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentWorkspaceId } = useAuth();
  const { completed, isLoading: onbLoading } = useOnboardingComplete();
  const tree = useListTree(currentWorkspaceId ?? undefined);
  const workspace = useWorkspaceInfo();

  const rootNodeId = workspace.data?.root_node_id as string | undefined;
  const nonRootAgents = useMemo(
    () => (tree.data ?? []).filter((n) => n.type === "agent" && n.id !== rootNodeId && !n.archived_at),
    [tree.data, rootNodeId],
  );

  const loading = onbLoading || tree.isLoading || workspace.isLoading;
  const shouldRedirect = !loading && (completed === true || nonRootAgents.length > 0);

  useEffect(() => {
    if (shouldRedirect) navigate("/workforce", { replace: true });
  }, [shouldRedirect, navigate]);

  const currentStep = STEP_BY_PATH[location.pathname] ?? 1;

  // While deciding (or redirecting away), render nothing over the canvas.
  if (loading || shouldRedirect) {
    return (
      <div style={pageContainer}>
        <span style={{ fontSize: 13, color: "var(--color-text-tertiary)" }}>Loading…</span>
      </div>
    );
  }

  return (
    <div style={pageContainer}>
      <div style={stepCard}>
        <StepIndicator current={currentStep} total={3} />
        <div style={contentBody}>
          <Outlet />
        </div>
      </div>
    </div>
  );
}

const pageContainer: CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 150,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "100vh",
  background: "var(--color-background-tertiary)",
  padding: "40px 20px",
  fontFamily: "var(--font-sans)",
};

const stepCard: CSSProperties = {
  background: "var(--color-background-primary)",
  border: "0.5px solid var(--color-border-tertiary)",
  borderRadius: "var(--border-radius-lg)",
  width: 480,
  maxWidth: "100%",
  minHeight: 540,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const contentBody: CSSProperties = {
  flex: 1,
  padding: "24px 28px",
  display: "flex",
  flexDirection: "column",
  gap: 14,
  minHeight: 0,
};
